/**
 * L'ÉCRAN DE DÉPLOIEMENT — ce que l'écran CALCULE, séparé de ce qu'il affiche.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * ── Pourquoi ce fichier existe ─────────────────────────────────────────────
 *
 * Le déploiement se réglait UNIQUEMENT par script depuis le 26/08/2026 :
 * `POST /ged/deploiement` existait, était gardé, fermait pour tout le monde —
 * et aucun écran ne l'appelait. Six rubriques ont été fermées par script, et
 * personne ne pouvait les rouvrir depuis l'application.
 *
 * Ce module porte les trois calculs de l'écran. Il est PUR — aucun React,
 * aucun `fetch` — pour que les trois soient vérifiables par `npm test` sans
 * navigateur et sans session.
 *
 * ── ⚠️ CE MODULE NE DÉCIDE RIEN ────────────────────────────────────────────
 *
 * `motifSuffisant` et `dateValide` sont des MIROIRS de la garde serveur
 * (`declarerDeploiement`), pas la garde elle-même. Ils servent à griser un
 * bouton et à écrire le reproche AVANT l'aller-retour ; le serveur refuse de
 * toute façon. Si les deux divergent un jour, c'est le serveur qui a raison —
 * et c'est le test `qualite-deploiement-ecran` qui le fera remarquer, en
 * confrontant `apercuPhrase` à `phraseAttente` du moteur pour les mêmes
 * entrées.
 *
 * ── ⚠️ « PAS ENCORE DÉPLOYÉ » N'EST PAS « PAS AUTORISÉ » ───────────────────
 *
 * L'écran vit dans le groupe « Rôles & accès » parce que c'est là que la RQ va
 * le chercher — mais le déploiement n'est PAS un droit. Il est global, il
 * ferme au RQ comme aux autres, et les droits restent intacts dessous : c'est
 * ce qui permet de rouvrir une rubrique d'un seul geste sans avoir à se
 * rappeler qui devait y avoir accès. Le bandeau de l'écran le dit ; ce
 * commentaire dit pourquoi il le dit.
 */

/** Une rubrique telle que `GET /ged/deploiement` la rend. */
export type RubriqueDeploiement = {
  code: string;
  famille: string;
  label: string;
  enService: boolean;
  /** La phrase servie par le moteur quand la rubrique est en attente. */
  phrase?: string;
  motif?: string;
  datePrevue?: string;
  parNom?: string;
  le?: string;
};

export type FamilleDeploiement = { famille: string; rubriques: RubriqueDeploiement[] };

/**
 * Regroupe par famille EN CONSERVANT L'ORDRE DU CATALOGUE.
 *
 * ⚠️ Pas de tri alphabétique : l'ordre des familles est celui de la barre
 * latérale, et l'écran de déploiement doit se lire comme la navigation — sinon
 * on cherche « Paie » entre « Audits » et « CQI ». C'est la même règle que
 * l'écran des droits par section.
 */
export function grouperParFamille(rubriques: readonly RubriqueDeploiement[]): FamilleDeploiement[] {
  const par = new Map<string, RubriqueDeploiement[]>();
  for (const r of rubriques || []) {
    const f = String(r?.famille || '—');
    if (!par.has(f)) par.set(f, []);
    par.get(f)!.push(r);
  }
  return [...par.entries()].map(([famille, liste]) => ({ famille, rubriques: liste }));
}

/**
 * Le motif est-il recevable ? MIROIR de la garde serveur : au moins 10
 * caractères une fois les blancs retirés.
 *
 * ⚠️ Le seuil ne juge pas la qualité du texte, il écarte le motif vide et le
 * « ok ». Ce qui compte est dit ailleurs : c'est la phrase que LIRA la personne
 * qui tombe sur la rubrique, pas une note interne.
 */
export function motifSuffisant(motif: unknown): boolean {
  return String(motif ?? '').trim().length >= 10;
}

/** Date d'ouverture prévue : vide (rien d'annoncé) ou AAAA-MM-JJ. Miroir serveur. */
export function dateValide(date: unknown): boolean {
  const d = String(date ?? '').trim();
  return d === '' || /^\d{4}-\d{2}-\d{2}$/.test(d);
}

/**
 * Ce qui empêche de fermer, dit en toutes lettres. `null` = rien n'empêche.
 *
 * Une phrase et non un booléen : un bouton grisé sans raison est le défaut que
 * ce projet a déjà corrigé ailleurs (menu du profil). Qui ne peut pas cliquer
 * doit savoir ce qui manque.
 */
export function empechementFermeture(motif: unknown, datePrevue: unknown): string | null {
  if (!motifSuffisant(motif)) {
    return 'Écrivez d’abord le motif — c’est la phrase que lira la personne qui tombe sur la rubrique.';
  }
  if (!dateValide(datePrevue)) return 'La date d’ouverture s’écrit AAAA-MM-JJ, ou reste vide.';
  return null;
}

/**
 * LA PHRASE QUE VERRA L'UTILISATEUR, montrée à la RQ AVANT qu'elle ne ferme.
 *
 * ⚠️ Miroir EXACT de `phraseAttente` (backend/src/deploiement.ts) — c'est tout
 * l'intérêt : on ne demande pas d'écrire un motif à l'aveugle, on montre le
 * texte fini. Le test confronte les deux fonctions sur les mêmes entrées ; le
 * jour où le moteur change sa formulation, il échoue ici.
 */
export function apercuPhrase(libelle: string, motif: unknown, datePrevue: unknown): string {
  const base = `« ${libelle} » n’est pas encore déployée.`;
  const m = String(motif ?? '').trim();
  const d = String(datePrevue ?? '').trim();
  return `${base}${m ? ` ${m}` : ''}${d ? ` Ouverture prévue le ${d}.` : ''}`;
}

/**
 * Qui a fermé, et quand — rendu vide quand on ne le sait pas.
 *
 * ⚠️ « Fermée par — le — » ne se compose PAS : les six rubriques fermées le
 * 26/08 l'ont été par script, sans `parNom`. Afficher un gabarit à trous ferait
 * croire à une perte de données là où il n'y a qu'une origine différente.
 */
export function origineFermeture(r: Pick<RubriqueDeploiement, 'parNom' | 'le'>): string {
  const qui = String(r?.parNom || '').trim();
  const quand = String(r?.le || '').trim();
  if (qui && quand) return `Fermée par ${qui}, le ${quand}`;
  if (qui) return `Fermée par ${qui}`;
  if (quand) return `Fermée le ${quand}`;
  return '';
}
